import { useState, useCallback } from 'react'

interface UploadResult {
  success: boolean
  url?: string
  thumbnail_url?: string
  error?: string
}

interface UseImageUploadReturn {
  upload: (file: File) => Promise<UploadResult>
  isUploading: boolean
  progress: number
  error: string | null
}

export function useImageUpload(): UseImageUploadReturn {
  const [isUploading, setIsUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const upload = useCallback(async (file: File): Promise<UploadResult> => {
    setIsUploading(true)
    setProgress(0)
    setError(null)

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp']
    if (!validTypes.includes(file.type)) {
      setIsUploading(false)
      const errorMsg = 'Type de fichier non supporté. Utilisez JPEG, PNG, GIF ou WebP.'
      setError(errorMsg)
      return { success: false, error: errorMsg }
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setIsUploading(false)
      const errorMsg = 'Fichier trop volumineux (max 10MB)'
      setError(errorMsg)
      return { success: false, error: errorMsg }
    }

    const formData = new FormData()
    formData.append('file', file)

    try {
      setProgress(30)

      const csrfToken = document.querySelector<HTMLMetaElement>(
        'meta[name="csrf-token"]'
      )?.content

      const response = await fetch('/admin/images', {
        method: 'POST',
        body: formData,
        headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {},
      })

      setProgress(80)

      const result = await response.json()

      setProgress(100)

      if (result.success) {
        return result
      } else {
        setError(result.error || "Erreur lors de l'upload")
        return result
      }
    } catch (err) {
      const errorMsg = "Erreur lors de l'upload"
      setError(errorMsg)
      return { success: false, error: errorMsg }
    } finally {
      setIsUploading(false)
    }
  }, [])

  return { upload, isUploading, progress, error }
}
